import React, { useCallback, useEffect } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { clearAuthToken, signOutUserHelper } from 'src/helpers/authHelpers';
import { signOutUser } from 'src/redux/actions/authActions';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import { authorizationError } from 'src/redux/actions/authActions';
import { authSelector, selectedWorkspacesSelector, userRoleSelector } from 'src/redux/selectors';
import Routes from './Routes';
import { store } from 'src/redux/store';
import { useLogoutByTimer, useRefreshAuthentication, useUserValues } from 'src/hooks';
import { PAGES } from './constants/pages';
import { ACCOUNT_ROLES } from './constants/globalConstants';
import { getCurrentWorkspaceFromLocalStorage } from './utils/comon';
import { setSelectedWorkspace } from './redux/actions/workspacesActions';

Amplify.configure(awsconfig);

axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    if (error?.response?.status === 401) store.dispatch(authorizationError());
    throw error;
  }
);

axios.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const selectedWorkspace = state?.workspaces?.selected;
    const cognitoUserRole = state?.user?.signInUserSession?.accessToken?.payload?.['cognito:groups']
      ?.filter((item) => item.startsWith('Role:'))
      .map((item) => item.replace('Role:', ''))
      .join(', ') as ACCOUNT_ROLES;

    const userRole = state?.userRole?.role;

    if (cognitoUserRole === ACCOUNT_ROLES.MULTIUSER && userRole) {
      config.headers['x-multirole'] = userRole.toLowerCase();
    }

    if (selectedWorkspace?.workspace?.id) {
      config.headers['x-workspace-id'] = selectedWorkspace.workspace.id;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const App = (): JSX.Element => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, isAuthenticating } = useSelector(authSelector);
  const userRole = useSelector(userRoleSelector);
  const { email } = useUserValues();
  const storedWorkspace = useSelector(selectedWorkspacesSelector);

  const selectedWorkspace = getCurrentWorkspaceFromLocalStorage(email);

  useEffect(() => {
    if (!isAuthenticated || userRole !== ACCOUNT_ROLES.USER) return;

    if (storedWorkspace || !selectedWorkspace) return;

    dispatch(setSelectedWorkspace(selectedWorkspace));
  }, [isAuthenticated, dispatch, selectedWorkspace, userRole, storedWorkspace]);

  const onLogoutWithRedirect = useCallback(async () => {
    await signOutUserHelper();
    clearAuthToken();
    dispatch(signOutUser());
    push(PAGES.SIGN_IN);
  }, [dispatch, push]);

  useLogoutByTimer(onLogoutWithRedirect);
  useRefreshAuthentication(onLogoutWithRedirect);

  return !isAuthenticating && <Routes isAuth={isAuthenticated} role={userRole} selectedWorkspace={selectedWorkspace} />;
};

export default App;
